import { useTranslation } from 'react-i18next';
import Box from 'shell/components/Box';
import Button from 'shell/components/Button';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import { formatVideoTime } from 'utils/videoGrading';
import { SpeakerTag } from './EvidenceTags';

const highlightedText = (segment, matches, highlights, activeMatchIndex) => {
    const overlaps = (range) => range.charStart < segment.charEnd && range.charEnd > segment.charStart;
    const search = matches.map((range, index) => ({ ...range, index })).filter(overlaps);
    const evidence = highlights.filter(overlaps);
    const boundaries = [
        ...new Set([
            0,
            segment.text.length,
            ...[...search, ...evidence].flatMap((range) => [
                Math.max(0, range.charStart - segment.charStart),
                Math.min(segment.text.length, range.charEnd - segment.charStart),
            ]),
        ]),
    ].sort((a, b) => a - b);
    return boundaries.slice(0, -1).map((start, index) => {
        const end = boundaries[index + 1];
        const match = search.find(
            (range) => range.charStart <= start + segment.charStart && range.charEnd >= end + segment.charStart
        );
        const isEvidence = evidence.some(
            (range) => range.charStart <= start + segment.charStart && range.charEnd >= end + segment.charStart
        );
        const text = segment.text.slice(start, end);
        if (!match && !isEvidence) return text;
        return (
            <Box
                key={start}
                component={'mark'}
                data-match-index={match?.index}
                sx={(theme) => ({
                    color: 'inherit',
                    background: match
                        ? match.index === activeMatchIndex
                            ? theme.palette.ensightful.secondary[100]
                            : theme.palette.ensightful.success[100]
                        : theme.palette.ensightful.warning[200],
                    borderBottom: isEvidence ? `1px solid ${theme.palette.ensightful.warning[900]}` : undefined,
                })}
            >
                {text}
            </Box>
        );
    });
};

const VideoTranscript = ({ transcript, currentTime, matches, highlights, activeMatchIndex, onSeek }) => {
    const { t } = useTranslation('assignment');
    return (
        <Stack spacing={5} sx={{ px: 4, pb: 8 }}>
            {!transcript.blocks.length && (
                <Text color={'light'} role={'status'}>
                    {t('video-grading.transcript-unavailable', 'Transcript is not available yet.')}
                </Text>
            )}
            {transcript.blocks.map((block) => (
                <Stack key={block.id} direction={'row'} alignItems={'flex-start'} spacing={3}>
                    {block.start !== null && (
                        <Button
                            variant={'borderless'}
                            color={'neutral'}
                            sx={{ p: 0, minWidth: 44, flexShrink: 0 }}
                            aria-label={t('video-grading.play-from', 'Play from {{time}}', {
                                time: formatVideoTime(block.start),
                            })}
                            onClick={() => onSeek(block.start)}
                        >
                            {formatVideoTime(block.start)}
                        </Button>
                    )}
                    <Stack spacing={2} sx={{ minWidth: 0 }}>
                        <SpeakerTag label={block.speakerLabel} />
                        <Text component={'p'} sx={{ lineHeight: '1.75rem', whiteSpace: 'pre-wrap' }}>
                            {block.segments.map((segment) => {
                                const timed = segment.start !== null;
                                const active = timed && currentTime >= segment.start && currentTime < segment.end;
                                return (
                                    <Box
                                        key={segment.id}
                                        component={'span'}
                                        role={timed ? 'button' : undefined}
                                        tabIndex={timed ? 0 : undefined}
                                        data-video-segment={segment.id}
                                        aria-label={
                                            timed
                                                ? t('video-grading.play-from', 'Play from {{time}}', {
                                                      time: formatVideoTime(segment.start),
                                                  })
                                                : undefined
                                        }
                                        onClick={timed ? () => onSeek(segment.start) : undefined}
                                        onKeyDown={
                                            timed
                                                ? (event) => {
                                                      if (event.key === 'Enter' || event.key === ' ') {
                                                          event.preventDefault();
                                                          onSeek(segment.start);
                                                      }
                                                  }
                                                : undefined
                                        }
                                        sx={(theme) => ({
                                            cursor: timed ? 'pointer' : 'text',
                                            borderRadius: '8px',
                                            background: active ? theme.palette.ensightful.primary[100] : 'transparent',
                                            '&:hover': timed
                                                ? { boxShadow: `0 0 0 2px ${theme.palette.ensightful.primary[200]}` }
                                                : undefined,
                                            '&:focus-visible': {
                                                outline: `2px solid ${theme.palette.ensightful.secondary[400]}`,
                                                outlineOffset: '2px',
                                            },
                                        })}
                                    >
                                        {highlightedText(segment, matches, highlights, activeMatchIndex)}{' '}
                                    </Box>
                                );
                            })}
                        </Text>
                    </Stack>
                </Stack>
            ))}
        </Stack>
    );
};

export default VideoTranscript;
